// Must stay the first import: seals a reseller's customer-dashboard demo tab before anything reads storage.
import './components/reseller/demo-sandbox/boot';
import { installShellRoutingBridge } from './services/shellRoutingBridge';

if (import.meta.env.PROD) installShellRoutingBridge();
// Sentry — defer init based on page type
// Landing pages: delay 10s (visitors rarely trigger errors)
// Authenticated pages: load on idle (need error tracking sooner)
import { initSentry } from './services/sentry';
import { captureLandingReferral } from './services/referralCapture';
import { captureBlogArticleSnapshot } from './components/blog/blogArticleSnapshot';

captureLandingReferral();
const isAuthPage = /^\/(dashboard|admin|settings|wallet|projects|billing|profile|login|register)/.test(window.location.pathname);
const isLocalDesignPreview = import.meta.env.DEV && window.location.pathname === '/landing-redesign-preview';
if (!isLocalDesignPreview && !(window as unknown as { __TC_WL_DEMO__?: unknown }).__TC_WL_DEMO__) {
  if (isAuthPage) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => initSentry());
    } else {
      setTimeout(() => initSentry(), 2000);
    }
  } else {
    setTimeout(() => initSentry(), 10000);
  }
}

window.addEventListener('error', (e) => {
  const msg = e.message || '';
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed')) {
    if (!sessionStorage.getItem('reloaded_for_module_error')) {
      sessionStorage.setItem('reloaded_for_module_error', 'true');
      window.location.reload();
    }
  }
});

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './src/main.css';
import './components/landing/TrafficLandingV2.css';
import './components/QuietOutlineForms.css';
import QuietOutlineSelectBridge from './components/QuietOutlineSelectBridge';
import { brandConfig } from './config/brand';
import { initializeTheme } from './config/theme';

initializeTheme();

const rootStyle = document.documentElement.style;
rootStyle.setProperty('--brand-primary', brandConfig.primaryColor);
rootStyle.setProperty('--brand-primary-hover', brandConfig.primaryHover);

/*
 * Firebase is NOT loaded. Nothing in the application consumes it.
 *
 * `services/firebase.ts` initialises firebase/compat app + auth + firestore and
 * exports `auth` and `firestore`. Measured 2026-08-16: the only import of that
 * module anywhere in the codebase was the idle preload that used to stand here,
 * which imported it for its side effect alone — no file reads either export.
 * Authentication does not go through it: the API issues a JWT (`tgp_token`) and
 * Google sign-in uses Google Identity Services (`services/googleIdentity.ts`,
 * accounts.google.com/gsi/client). The only other occurrence of the string
 * "firebase" in src is `firebaseapp.com` inside a domain blocklist.
 *
 * Cost of keeping it: 511 KB raw / 130 KB gzip fetched and parsed on EVERY
 * authenticated page load, dashboard included — 28 % of all JS transferred on
 * the dashboard entry route, for code that never runs.
 *
 * The module is left in the tree rather than deleted so re-enabling it is a
 * normal import at the point of use, which also lets Rollup split it into the
 * route that needs it instead of the entry.
 */

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

captureBlogArticleSnapshot();

// The static defaults keep first-response HTML useful before React loads.
// Remove them before Helmet mounts so it never adopts and later loses a tag
// that still carries our static `data-default-seo` marker.
document.head
  .querySelectorAll('[data-default-seo="true"]')
  .forEach((tag) => tag.remove());

declare global {
  interface Window {
    __TC_APP_BOOTED__?: boolean;
    /** Path this document was loaded at; the dot-matrix homepage reloads itself when reached in-app. */
    __tcFirstPath?: string;
  }
}

if (!window.__TC_APP_BOOTED__) {
  window.__TC_APP_BOOTED__ = true;
  window.__tcFirstPath = window.location.pathname;
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <HelmetProvider>
      <BrowserRouter>
        <QuietOutlineSelectBridge />
        <App />
      </BrowserRouter>
    </HelmetProvider>
  );
}
